/* eslint-disable prettier/prettier */ // Prettier is messing with the curried handleDateAccept method, always gives an error after formatting

import styled from '@emotion/styled'
import ClearIcon from '@mui/icons-material/Clear'
import Autocomplete from '@mui/material/Autocomplete'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { colors } from 'themes'
import Spacings from '../../../figma/tokens/Spacings'

type DateRange = {
  from: string
  to: string
}

export type Filters = {
  text: string
  state: string
  invoiceDate: DateRange
  dueDate: DateRange
  period: DateRange
  hasPaid: boolean
  currency: string
  receiver: string
  type: string
}

export type SelectOption = { value: string; label: string }

export type RollingCreditFiltersProps = {
  filterValues?: Partial<Filters>
  onFilterChange: (filers: Partial<Filters>) => void
  dateFormat?: string
  options: {
    currency: SelectOption[]
    status: SelectOption[]
    receiver?: SelectOption[]
    type?: SelectOption[]
  }
}

const ClearButton = ({ hidden, onClick }: { hidden?: boolean; onClick?: () => void }) => {
  if (hidden) return null

  return (
    <IconButton onClick={onClick} sx={{ marginRight: 1 }}>
      <ClearIcon sx={{ width: 24, height: 24 }} />
    </IconButton>
  )
}

const parseHasPaidValue = (hasPaid?: boolean) => {
  if (hasPaid === undefined) return ''
  if (hasPaid) return 1

  return 0
}

const parseHasPaidChange = (value: unknown) => {
  if (value === '' || value == null) return undefined

  return Number(value) === 1
}

type MaterialDateAccept = [dayjs.Dayjs | null, dayjs.Dayjs | null]

type DateRangeKey = 'invoiceDate' | 'dueDate' | 'period'

const parseDates = (date: string | undefined) => (date ? dayjs(date) : null)

export const RollingCreditFilters = ({ filterValues, options, onFilterChange, dateFormat }: RollingCreditFiltersProps) => {
  const [filters, setFilters] = useState(filterValues ?? {})
  const [liveDateRanges, setLiveDateRanges] = useState<Partial<Record<DateRangeKey, MaterialDateAccept>>>({})

  const handleChange = (newState: Partial<Filters>) => setFilters((currentFilters) => ({ ...currentFilters, ...newState }))

  const getDateRangeValue = (key: DateRangeKey): MaterialDateAccept =>
    liveDateRanges[key] ?? [parseDates(filters?.[key]?.from), parseDates(filters?.[key]?.to)]

  const handleDateAccept =
    (key: DateRangeKey) =>
    ([from, to]: MaterialDateAccept) => {
      if (from && to) handleChange({ [key]: { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') } })
      setLiveDateRanges((prev) => ({ ...prev, [key]: undefined }))
    }

  const handleDateChange = (key: DateRangeKey) => (dates: MaterialDateAccept) => {
    setLiveDateRanges((prev) => ({ ...prev, [key]: dates }))
    if (dates.every((date) => date === null)) handleChange({ [key]: undefined })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    onFilterChange(filters)
  }, [filters])

  useEffect(() => {
    if (filterValues) {
      setFilters(filterValues)
      setLiveDateRanges({})
    }
  }, [filterValues])

  return (
    <BorderedContainer>
      <Header>
        <ColumnContainer>
          <Typography>Rolling credit filters</Typography>
        </ColumnContainer>
        <ColumnContainer></ColumnContainer>
      </Header>
      <RowContainer>
        <TextField
          fullWidth
          data-testid="text-search"
          onChange={(event) => handleChange({ text: event.target.value })}
          label="Search on filtered results..."
        />
      </RowContainer>
      <RowContainer>
        <ColumnContainer>
          <FormControl>
            <InputLabel id="Status">Status</InputLabel>
            <Select
              data-testid="status"
              value={filters?.state ?? ''}
              onChange={(e) => handleChange({ state: e.target.value })}
              style={{ minWidth: 130 }}
              labelId="Status"
              label="Status"
              IconComponent={() => <ClearButton onClick={() => handleChange({ state: undefined })} hidden={!filters?.state} />}
            >
              {options.status.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ColumnContainer>
        <ColumnContainer>
          <FormControl>
            <InputLabel id="account-type">Account type</InputLabel>
            <Select
              data-testid="account-type"
              value={filters?.type ?? ''}
              onChange={(e) => handleChange({ type: e.target.value })}
              style={{ minWidth: 130 }}
              labelId="account-type"
              label="Account type"
              IconComponent={() => <ClearButton onClick={() => handleChange({ type: undefined })} hidden={!filters?.type} />}
            >
              {(options.type ?? []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ColumnContainer>
        <ColumnContainer>
          <Autocomplete
            data-testid="receiver"
            options={options.receiver ?? []}
            value={(options.receiver ?? []).find((option) => option.value === filters?.receiver) ?? null}
            onChange={(_, option) => handleChange({ receiver: option?.value ?? undefined })}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label="Receiver" />}
            sx={{
              minWidth: 130,
              // Match the themed sibling inputs: zero the Autocomplete's own root padding and give the
              // input the same 12.5px vertical padding the theme applies to other outlined inputs.
              '& .MuiAutocomplete-inputRoot': { paddingTop: 0, paddingBottom: 0 },
              '& .MuiAutocomplete-input': { paddingTop: '12.5px !important', paddingBottom: '12.5px !important' },
            }}
          />
        </ColumnContainer>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
          <ColumnContainer>
            <DateRangePicker
              format={dateFormat}
              value={getDateRangeValue('invoiceDate')}
              onAccept={handleDateAccept('invoiceDate')}
              onChange={handleDateChange('invoiceDate')}
              label="Invoice date"
              slotProps={{
                actionBar: { actions: ['clear'] },
                textField: { style: { minWidth: 315 } },
                field: { 'data-testid': 'invoice-date' } as any,
                openPickerButton: { sx: { marginRight: Spacings.minimum } },
              }}
              slots={{ field: SingleInputDateRangeField }}
            />
          </ColumnContainer>
          <ColumnContainer>
            <DateRangePicker
              format={dateFormat}
              value={getDateRangeValue('dueDate')}
              onAccept={handleDateAccept('dueDate')}
              onChange={handleDateChange('dueDate')}
              label="Due date"
              slotProps={{
                actionBar: { actions: ['clear'] },
                textField: { style: { minWidth: 315 } },
                field: { 'data-testid': 'due-date' } as any,
                openPickerButton: { sx: { marginRight: Spacings.minimum } },
              }}
              slots={{ field: SingleInputDateRangeField }}
            />
          </ColumnContainer>
          <ColumnContainer>
            <DateRangePicker
              format={dateFormat}
              value={getDateRangeValue('period')}
              onAccept={handleDateAccept('period')}
              onChange={handleDateChange('period')}
              label="Period"
              slotProps={{
                actionBar: { actions: ['clear'] },
                textField: { style: { minWidth: 315 } },
                field: { 'data-testid': 'period' } as any,
                openPickerButton: { sx: { marginRight: Spacings.minimum } },
              }}
              slots={{ field: SingleInputDateRangeField }}
            />
          </ColumnContainer>
        </LocalizationProvider>
        <ColumnContainer>
          <FormControl>
            <InputLabel id="currency">Currency</InputLabel>
            <Select
              data-testid="currency"
              value={filters?.currency ?? ''}
              onChange={(e) => handleChange({ currency: e.target.value })}
              style={{ minWidth: 130 }}
              labelId="currency"
              label="Currency"
              IconComponent={() => <ClearButton onClick={() => handleChange({ currency: undefined })} hidden={!filters?.currency} />}
            >
              {options.currency.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ColumnContainer>
        <ColumnContainer>
          <FormControl>
            <InputLabel id="has-paid">Has paid</InputLabel>
            <Select
              data-testid="has-paid"
              value={parseHasPaidValue(filters.hasPaid)}
              onChange={(e) => handleChange({ hasPaid: parseHasPaidChange(e.target.value) })}
              style={{ minWidth: 130 }}
              labelId="has-paid"
              label="has-paid"
              IconComponent={() => (
                <ClearButton onClick={() => handleChange({ hasPaid: undefined })} hidden={filters?.hasPaid === undefined} />
              )}
            >
              <MenuItem value={1}>true</MenuItem>
              <MenuItem value={0}>false</MenuItem>
            </Select>
          </FormControl>
        </ColumnContainer>
      </RowContainer>
    </BorderedContainer>
  )
}

const BorderedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 24px;
  border: 1px solid ${colors.product.grey[400]};
  border-radius: 8px;
`

const ColumnContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`
